import dynamic from 'next/dynamic';
import React, { Fragment } from 'react';
import { messageFormat } from '@clubmed/utils';
import type { Props as HeroProps } from 'Components/widgets/hero/Hero';
import { Hero } from 'Components/widgets/hero/Hero';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import type { LineProps } from 'Components/frameworks/line/Line';
import 'Components/frameworks/line/Line';
import { useAppContext } from 'Context/ReactAppContext';
import { useRouter } from 'next/router';
import { formatLink } from 'Utils/uri/uriUtils';
import { getMinPrice } from './ImmersiveLine.utils';
const FacetSearch = dynamic(() =>
  import('Components/widgets/facet-search/FacetSearch').then((x) => x.FacetSearch),
);
type Props = HeroProps & {
  config: CMSContent;
  dynamicPrice?: {
    title?: string;
    subtitle?: string;
    link?: {
      url: string;
      label: string;
    };
    displayDynamicPrice: boolean;
  };
  displaySearch: boolean;
  id?: string;
  isExclusiveCollection: boolean;
  pageLines?: ReadonlyArray<LineProps>;
};

const formatLabelWithPrice = ({ label, price }: { label?: string; price: string }) => {
  return messageFormat(label || '').format({
    best_price_on_page: price,
  });
};

export const ImmersiveLine = ({
  config,
  dynamicPrice = {},
  displaySearch = false,
  id,
  pageLines = [],
  ...props
}: Props) => {
  const appContext = useAppContext();
  const router = useRouter();
  const { displayDynamicPrice } = dynamicPrice;
  const minPrice = displayDynamicPrice
    ? getMinPrice({
        props: {
          lines: pageLines,
        },
      })
    : undefined;
  const formattedMinPrice = minPrice?.bestPrice?.formattedValue || '';
  const dynamicPriceLabels =
    displayDynamicPrice && !!formattedMinPrice
      ? {
          title: formatLabelWithPrice({
            label: dynamicPrice.title,
            price: formattedMinPrice,
          }),
          subtitle: formatLabelWithPrice({
            label: dynamicPrice.subtitle,
            price: formattedMinPrice,
          }),
          link: {
            url: dynamicPrice.link?.url || '',
            label: formatLabelWithPrice({
              label: dynamicPrice.link?.label,
              price: formattedMinPrice,
            }),
          },
        }
      : {};
  const newLink = {
    ...props.link,
    url: props.link?.url
      ? formatLink(props.link?.url, appContext.originalUrl, router.query.locale)
      : '',
  };
  return (
    <Fragment>
      <div className="Line Line-Immersive" id={id}>
        <Hero
          {...props}
          link={newLink}
          hasOverlap={displaySearch}
          isImmersive
          {...dynamicPriceLabels}
          as="a"
        />
      </div>
      {displaySearch && (
        <SiteContainer className="Line Line-Search relative isolate z-5">
          <FacetSearch
            i18n={config}
            searchPageRouteName="search"
            shouldRedirectToResortPage
            withSearchForm
          />
        </SiteContainer>
      )}
    </Fragment>
  );
};
